// @flow
import React from 'react';
import { intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import creationMessages from '../../../data/product-creation';
import RoundButton from '../round-button/RoundButtonComponent';
import PlusIcon from '../icons/plus/PlusComponent';
import UploadIcon from '../icons/upload/UploadComponent';
import TemplateIcon from '../icons/template/TemplateComponent';
import { envUrl } from '../../services/environment/EnvironmentService';
import SplitButton from '../split-button/SplitButtonComponent';
import Tooltip from '../tooltip/TooltipComponent';
import {
  canCreateProductFromTemplate,
  canCreateProductByFullUpload,
} from '../../services/user-permission/UserPermissionService';
import { areTemplatesAvailable } from '../../services/product-type/ProductTypeService';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import productEvents from '../../../analytics/events/products';

import './NewProductButton.scss';

type Props = {
  tooltipAlwaysActive: boolean,
  config: {
    permissions: Array<string>,
  },
};

type NewProductButtonOption = {
  icon: Element,
  label: string,
  link: string,
  id: string,
  onClick: Function,
};

class NewProductButton extends React.Component<Props> {
  static contextTypes = {
    intl: intlShape.isRequired,
  };

  constructor(props: Props) {
    super(props);
    this.state = { showTooltip: props.tooltipAlwaysActive || false };
  }

  componentDidMount() {
    this.isComponentMounted = true;
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
  }

  getNewProductButtonOptions() {
    const hasTemplatesPermission = canCreateProductFromTemplate(this.props.config.permissions);
    const hasFullUploadPermission = canCreateProductByFullUpload(this.props.config.permissions);
    const hasTemplates = areTemplatesAvailable(this.props.config.productSizesAvailable);

    const options = [];

    if (hasTemplatesPermission && hasTemplates) {
      options.push({
        icon: <TemplateIcon />,
        label: this.context.intl.formatMessage(creationMessages.createProductWithTemplate),
        link: '/products/create',
        id: 'create',
        onClick: () => AnalyticsClient.trackEvent(productEvents.createNewProduct('Template')),
      });
    }

    if (hasFullUploadPermission) {
      options.push({
        icon: <UploadIcon />,
        label: this.context.intl.formatMessage(creationMessages.createProductFullUpload),
        link: '/products/guidelines',
        id: 'upload',
        onClick: () => AnalyticsClient.trackEvent(productEvents.createNewProduct('Full Upload')),
      });
    }

    return options;
  }

  showProductTooltip = () => {
    if (this.isComponentMounted && !this.state.showTooltip) {
      this.setState({ showTooltip: true });
    }
  };

  hideProductTooltip = () => {
    if (this.isComponentMounted && this.state.showTooltip) {
      this.setState({ showTooltip: this.props.tooltipAlwaysActive || false });
    }
  };

  handleSplitButtonChange = (isOpen: boolean) => {
    if (this.isComponentMounted) {
      this.setState({ showTooltip: !isOpen });
    }
  };

  getButton = (options: Array<NewProductButtonOption>) => {
    if (options.length === 0) {
      return <RoundButton type="primary" size="small" disabled icon={<PlusIcon />} />;
    }

    if (options.length === 1) {
      return (
        <Link to={envUrl(options[0].link)}>
          <RoundButton
            type="primary"
            size="small"
            icon={<PlusIcon />}
            onClick={() => {
              options[0].onClick();
            }}
          />
        </Link>
      );
    }

    return (
      <SplitButton
        icon={<PlusIcon />}
        actions={this.props.actions}
        onChange={this.handleSplitButtonChange}
        options={options}
      />
    );
  };

  getTooltipText = (options: Array<NewProductButtonOption>) => {
    if (options.length === 0) {
      return this.context.intl.formatMessage(creationMessages.createNewProductDisabledTooltip);
    }

    return this.context.intl.formatMessage(creationMessages.createNewProductTooltip);
  };

  render() {
    const options = this.getNewProductButtonOptions();

    return (
      <div
        className="new-product-button-component__button"
        onMouseEnter={this.showProductTooltip}
        onMouseLeave={this.hideProductTooltip}
      >
        <Tooltip position="right" label={this.getTooltipText(options)} isActive={this.state.showTooltip} />
        {this.getButton(options)}
      </div>
    );
  }
}

export default NewProductButton;
